// Location intelligence. Coordinates and approximate elevations are geographic
// references; regulatory and market statements are deliberately framed as
// considerations and marked for sourcing rather than stated as verified fact.
// status: "verified" | "draft" | "source" (REQUIRES-SOURCE)

export const LOCATIONS = [
  {
    slug: "dehradun",
    name: "Dehradun",
    region: "Doon Valley · Garhwal",
    district: "Dehradun",
    coords: "30.3165° N · 78.0322° E",
    elevation: "~640 m ASL",
    image: "dehradun",
    tagline: "The valley capital — infrastructure, institutions and the broadest property market in the state.",
    intro:
      "Dehradun sits on the floor of the Doon Valley between the Shivalik range and the Mussoorie massif. As the state capital it carries the deepest infrastructure, the widest spread of property types and, consequently, the most complex planning and regulatory landscape in Uttarakhand.",
    quickFacts: [
      { label: "Setting", value: "Valley floor, foothills" },
      { label: "Governing context", value: "MDDA jurisdiction (verify by location)" },
      { label: "Typical buyers", value: "Residence, second home, investment" },
      { label: "Access", value: "Rail, road, Jolly Grant airport" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "The Doon Valley is a gently graded basin, which makes much of Dehradun atypical of Uttarakhand — large parts are buildable flat land rather than steep slope. This shapes everything from plot formats to construction cost.",
          "The character shifts sharply across the city: the older core, the institutional belt, the orchard fringes to the north, and the premium corridor rising toward Mussoorie each behave as distinct micro-markets.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Dehradun is among the best-connected points in the state — a railhead, an expanding road network toward Delhi, and Jolly Grant airport to the south-east. Connectivity is a primary reason the Doon market is deeper and more liquid than the hill markets above it.",
        ],
        list: [
          "Confirm the specific access road status and width for any plot",
          "Check seasonal waterlogging on valley-floor sites",
          "Verify distance and realistic travel time to the airport and railhead",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Most of urban Dehradun falls under a development authority whose master plan assigns land use and building norms. The exact authority, zone and permissible use must be confirmed for the specific khasra/plot — city-wide generalisations are unreliable here.",
          "Land classification (agricultural vs non-agricultural) and any applicable purchase restrictions should be verified against current revenue records and state rules before proceeding.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "Because Dehradun spans flat valley land and rising slopes, two plots a few kilometres apart can carry entirely different construction, drainage and approval realities. Treat each micro-location on its own terms.",
          "The premium northern corridor trades on proximity to Mussoorie and institutional Dehradun; verify that the premium is supported by access, title and planning status rather than narrative alone.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "The valley enjoys a milder climate than the plains but is sensitive to its rivers and seasonal streams (raus). Proximity to a watercourse, floodplain history and slope stability on the valley edges are material to both safety and value.",
        ],
      },
    ],
    dueDiligence: [
      "Which development authority and master-plan zone governs this exact plot?",
      "Is the land agricultural or non-agricultural, and what does that permit for your intended use?",
      "Is there a clear, legally recorded access road to the plot?",
      "What is the flood / seasonal-stream history of the immediate area?",
      "Do revenue records (khatauni) and the sale chain reconcile cleanly?",
    ],
  },
  {
    slug: "mussoorie",
    name: "Mussoorie",
    region: "Garhwal Himalaya",
    district: "Dehradun",
    coords: "30.4599° N · 78.0664° E",
    elevation: "~2,000 m ASL",
    image: "mussoorie",
    tagline: "The ridge above the Doon — scarce land, strong demand and demanding terrain.",
    intro:
      "Mussoorie occupies a high ridge overlooking the Doon Valley. Buildable land is genuinely scarce, slopes are steep, and ecological and heritage sensitivities are significant. It is a market where constraints — not availability — define opportunity.",
    quickFacts: [
      { label: "Setting", value: "High ridge, steep slope" },
      { label: "Land availability", value: "Constrained / scarce" },
      { label: "Typical buyers", value: "Second home, hospitality" },
      { label: "Key risk", value: "Slope, access, sanction limits" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "Mussoorie is almost entirely sloped terrain. The practical consequence is that site selection is dominated by gradient, aspect, stability and access — far more than plot size or view alone.",
          "The town carries a long colonial-era settlement history, which means established built fabric, heritage considerations and a tightly held land base.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Access is via the climb from Dehradun. For any property the last-mile matters most: whether a vehicle can reach it, how services are carried up, and how the approach behaves in monsoon and winter.",
        ],
        list: [
          "Walk the actual approach, not just the map line",
          "Confirm vehicular access and parking reality",
          "Assess monsoon landslide and winter conditions on the route",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Hill towns like Mussoorie are subject to building regulations and environmental sensitivities that can restrict height, coverage and cutting of slope. Permissible construction must be confirmed for the specific site with the governing authority.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "A beautiful view does not guarantee a buildable site. Geotechnical stability, drainage across the slope, and the legality of any existing structure are the first questions, not the last.",
          "Scarcity supports value, but it also attracts informal arrangements. Insist on clean, recorded title and sanctioned construction.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Steep Himalayan slope brings landslide, drainage and seismic considerations. These are not reasons to avoid Mussoorie — they are reasons to engage qualified assessment before committing.",
        ],
      },
    ],
    dueDiligence: [
      "Is the slope stable, and has any geotechnical assessment been done?",
      "Can a vehicle actually reach the site, year-round?",
      "Is existing construction sanctioned and within permissible limits?",
      "What environmental or heritage restrictions apply to this ridge?",
      "How are water and power realistically supplied and maintained?",
    ],
  },
  {
    slug: "rishikesh",
    name: "Rishikesh",
    region: "Garhwal · Ganga foothills",
    district: "Dehradun / Tehri edge",
    coords: "30.0869° N · 78.2676° E",
    elevation: "~370 m ASL",
    image: "rishikesh",
    tagline: "Where the Ganga leaves the mountains — wellness, hospitality and riverine sensitivity.",
    intro:
      "Rishikesh sits where the Ganga emerges from the hills onto the plains. Its identity is bound to the river and to a global wellness and hospitality economy, which gives its property market a distinct, demand-led character — and distinct riverine and regulatory constraints.",
    quickFacts: [
      { label: "Setting", value: "River foothills" },
      { label: "Economy", value: "Wellness, hospitality, pilgrimage" },
      { label: "Typical buyers", value: "Hospitality, second home" },
      { label: "Key risk", value: "River setbacks, zoning" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "The town spans both riverbanks and the forested foothills around them. Land near the river is the most sought-after and the most constrained; the surrounding slopes offer more room but different access and services.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Rishikesh is well connected to Haridwar and Dehradun by road and rail, with ongoing regional infrastructure investment. Its accessibility underpins a resilient hospitality demand base.",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Riverfront and forest-adjacent land can attract setbacks, environmental clearances and use restrictions. Any plan involving commercial hospitality use requires careful verification of permitted land use and licensing.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "If the intended use is hospitality or wellness, the property's commercial permissibility matters as much as its title. Confirm that the planned use is actually allowed on the land before valuing it as a business.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Proximity to the Ganga is the defining feature and the defining risk — flood lines, setbacks and ecological protections should be established early for any riverside site.",
        ],
      },
    ],
    dueDiligence: [
      "Is the intended (often commercial) use permitted on this land?",
      "Where is the river setback / flood line relative to the plot?",
      "Are forest or environmental clearances implicated?",
      "What licences does a hospitality operation require here?",
      "Is the title clean and free of religious-trust or disputed interests?",
    ],
  },
  {
    slug: "nainital",
    name: "Nainital",
    region: "Kumaon Himalaya",
    district: "Nainital",
    coords: "29.3919° N · 79.4542° E",
    elevation: "~2,084 m ASL",
    image: "nainital",
    tagline: "The lake town of Kumaon — mature, sensitive and tightly bounded.",
    intro:
      "Built around its namesake lake, Nainital is Kumaon's best-known hill town. It is a mature, environmentally sensitive market where the lake, the surrounding slopes and carrying-capacity concerns shape what is possible.",
    quickFacts: [
      { label: "Setting", value: "Lake basin, steep slopes" },
      { label: "Land availability", value: "Highly constrained" },
      { label: "Typical buyers", value: "Second home, legacy" },
      { label: "Key risk", value: "Slope, lake-zone sensitivity" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "The town wraps around a lake in a steep bowl. Core availability is minimal; most contemporary activity is in the broader Nainital belt and satellite ridges rather than the lake edge itself.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Access is via the climb from the Kumaon foothills. As with other hill towns, the quality and resilience of the final approach to a specific property is decisive.",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Lake-zone and slope sensitivities commonly bring restrictions on construction near the water and on steep ground. Confirm the applicable zone and permissible activity for the specific plot.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "In a tightly bounded market, the temptation is to accept compromise on title or access to secure scarce land. Resist it — the constraints that create scarcity also punish mistakes.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Slope stability and the ecological status of the lake catchment are material. Monsoon behaviour of the approach and the site should be understood directly.",
        ],
      },
    ],
    dueDiligence: [
      "What zone governs the plot, and does it restrict construction?",
      "Is the slope stable and the drainage sound?",
      "Is there clean, recorded title and legal access?",
      "How does the approach perform in monsoon?",
      "Are there lake-catchment or environmental protections in play?",
    ],
  },
  {
    slug: "mukteshwar",
    name: "Mukteshwar",
    region: "Kumaon Himalaya",
    district: "Nainital",
    coords: "29.4722° N · 79.6490° E",
    elevation: "~2,170 m ASL",
    image: "mukteshwar",
    tagline: "Orchard-country ridges with long snow-range views — a quieter second-home market.",
    intro:
      "Mukteshwar and its surrounding ridges have become a favoured quieter alternative to the busier Kumaon towns, known for orchard land, pine forest and expansive Himalayan views. It is substantially a second-home and land market.",
    quickFacts: [
      { label: "Setting", value: "Orchard ridges, forest" },
      { label: "Character", value: "Quiet, view-led" },
      { label: "Typical buyers", value: "Second home, land, retreat" },
      { label: "Key risk", value: "Access, services, land category" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "The area is a patchwork of orchard land, forest and small settlements along ridgelines. Views are a primary driver of value, which makes aspect and the protection of the view corridor important to assess.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "This is deeper hill country — travel times from the foothills are longer and last-mile roads are more variable. Access resilience is central to whether a property is practical as a second home.",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Much land here is agricultural/orchard in classification. The rules governing who may buy such land and for what use must be verified carefully, as they materially affect feasibility.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "A view can be bought; it cannot always be protected. Consider what could be built on adjacent land, and whether water, power and road can realistically serve the site through the seasons.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Forest-edge locations carry both amenity and responsibility — fire season, wildlife and water sourcing all feature in genuine hill living.",
        ],
      },
    ],
    dueDiligence: [
      "What is the land's classification, and does it permit your intended use?",
      "Is the approach road legally recorded and physically reliable?",
      "How are water and power sourced and sustained year-round?",
      "Could adjacent construction compromise the view or access?",
      "Does the sale chain and revenue record reconcile?",
    ],
  },
  {
    slug: "almora",
    name: "Almora",
    region: "Kumaon Himalaya",
    district: "Almora",
    coords: "29.5892° N · 79.6467° E",
    elevation: "~1,600 m ASL",
    image: "almora",
    tagline: "A cultural heartland of Kumaon — heritage settlement and terraced hill land.",
    intro:
      "Almora is one of Kumaon's historic towns, set along a curving ridge with deep cultural roots and a surrounding landscape of terraced hill land and villages. Its market blends heritage settlement with emerging second-home interest.",
    quickFacts: [
      { label: "Setting", value: "Ridge town, terraced hills" },
      { label: "Character", value: "Heritage, cultural" },
      { label: "Typical buyers", value: "Second home, land, retreat" },
      { label: "Key risk", value: "Land category, access, title chain" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "The town itself has a dense historic core; the opportunity for most buyers lies in the surrounding ridges and villages, where terraced land and forest frame the Kumaon landscape.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Almora is a regional hub within Kumaon, but individual properties can be well off the main road. The specific last-mile determines practicality.",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Rural and agricultural land rules, and any restrictions applicable to non-local buyers, are especially relevant here and must be checked against current state provisions.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "Village and terraced land often carries fragmented ownership and customary arrangements. A careful title and revenue-record study is essential, not optional.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Terraced hill land has its own water, slope and cultivation logic. Understanding how a plot sits within that system helps avoid surprises.",
        ],
      },
    ],
    dueDiligence: [
      "Is ownership consolidated, or fragmented across several parties?",
      "What does the land's classification permit?",
      "Are there restrictions on purchase for your buyer category?",
      "Is there legal, recorded access to the plot?",
      "How is water sourced for the land?",
    ],
  },
  {
    slug: "ranikhet",
    name: "Ranikhet",
    region: "Kumaon Himalaya",
    district: "Almora",
    coords: "29.6434° N · 79.4322° E",
    elevation: "~1,870 m ASL",
    image: "ranikhet",
    tagline: "Forested cantonment ridges with protected character and long peak views.",
    intro:
      "Ranikhet is a forested ridge town with a strong cantonment presence, which has helped preserve its green, low-density character and its long views toward the high Himalaya. That same character shapes a particular and often restrictive land market.",
    quickFacts: [
      { label: "Setting", value: "Forested ridges, cantonment" },
      { label: "Character", value: "Green, low-density" },
      { label: "Typical buyers", value: "Second home, retreat" },
      { label: "Key risk", value: "Cantonment / forest restrictions" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "The town's green, preserved feel owes much to its cantonment and forest cover. Private land tends to sit on the surrounding civilian ridges rather than within protected or military-administered areas.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Ranikhet is reached through the Kumaon hill network. As elsewhere, the resilience of the final approach is the practical determinant for a second home.",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "Cantonment-adjacent and forest-adjacent land can carry particular restrictions. Confirm exactly which jurisdiction and rules apply to a specific plot before relying on any assumption.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "The protected character that makes Ranikhet attractive is also what constrains supply and activity. Verify that any plot is genuinely private, transferable and free of restriction.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Dense forest cover brings fire-season, wildlife and water-sourcing considerations familiar across Kumaon's higher ground.",
        ],
      },
    ],
    dueDiligence: [
      "Is the plot genuinely private and freely transferable?",
      "Do cantonment or forest restrictions affect it?",
      "Is there recorded legal access?",
      "How are services realistically provided?",
      "Does the title chain and revenue record reconcile?",
    ],
  },
  {
    slug: "haridwar",
    name: "Haridwar",
    region: "Ganga plains · foothills gateway",
    district: "Haridwar",
    coords: "29.9457° N · 78.1642° E",
    elevation: "~314 m ASL",
    image: "haridwar",
    tagline: "The gateway where the plains meet the hills — pilgrimage, industry and connectivity.",
    intro:
      "Haridwar sits at the threshold between the Gangetic plains and the Himalayan foothills. Its property market is driven by pilgrimage, established industry and strong connectivity rather than by hill-country scarcity.",
    quickFacts: [
      { label: "Setting", value: "Plains / foothills gateway" },
      { label: "Economy", value: "Pilgrimage, industry" },
      { label: "Typical buyers", value: "Residence, commercial" },
      { label: "Key risk", value: "River zone, land use" },
    ],
    sections: [
      {
        id: "overview",
        heading: "Overview & Terrain",
        status: "verified",
        body: [
          "Largely flat and plains-like, Haridwar behaves differently from the hill markets — construction is more conventional, and the drivers are connectivity, industry and the pilgrimage economy.",
        ],
      },
      {
        id: "connectivity",
        heading: "Connectivity & Access",
        status: "verified",
        body: [
          "Haridwar is a major rail and road node connecting the plains to the Himalayan corridor, which supports steady demand across residential and commercial segments.",
        ],
      },
      {
        id: "planning",
        heading: "Master Plan & Land Regulation",
        status: "source",
        body: [
          "River-zone protections and local land-use designations are the key regulatory factors. Confirm the applicable authority, zone and permissions for the specific plot.",
        ],
      },
      {
        id: "buyer",
        heading: "Buyer Considerations",
        status: "draft",
        body: [
          "As a plains-adjacent market, Haridwar is more liquid but also more conventional — the premium of hill scarcity does not apply, so value rests on location, use and clean title.",
        ],
      },
      {
        id: "environment",
        heading: "Environment & Topography",
        status: "verified",
        body: [
          "Proximity to the Ganga brings flood-line and setback considerations for riverside land, alongside the usual plains drainage factors.",
        ],
      },
    ],
    dueDiligence: [
      "What land use does the plot's zone permit?",
      "Is the plot affected by river setbacks or flood lines?",
      "Which authority governs approvals here?",
      "Is title clean and free of trust / disputed interests?",
      "Does the intended use match permitted use?",
    ],
  },
];

export const REGION_GROUPS = [
  { key: "doon", label: "Doon Valley & Garhwal", slugs: ["dehradun", "mussoorie", "rishikesh", "haridwar"] },
  { key: "kumaon", label: "Kumaon Himalaya", slugs: ["nainital", "mukteshwar", "almora", "ranikhet"] },
];

export function getLocation(slug) {
  return LOCATIONS.find((l) => l.slug === slug);
}

export default LOCATIONS;
